import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { wholesaleService } from '../../services/wholesaleService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const OrderHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    fetchOrders();
    fetchProfile();
    fetchPaymentMethods();
  }, []);

  const fetchOrders = async () => {
    try {
        const response = await wholesaleService.getOrders();
        // Check if response has the expected structure
        if (response && response.orders) {
            setOrders(response.orders);
        } else {
            throw new Error('Invalid response format');
        }
    } catch (error) {
        setError('Failed to fetch orders');
        console.error('Error fetching orders:', error);
    } finally {
        setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const profileData = await wholesaleService.getProfile();
      setProfile(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const methods = await wholesaleService.getPaymentMethods();
      setPaymentMethods(methods);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (error) throw error;

      await wholesaleService.addPaymentMethod(paymentMethod.id);
      await fetchPaymentMethods();
      setShowNewCardForm(false);
      elements.getElement(CardElement).clear();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    try {
      await wholesaleService.deletePaymentMethod(paymentMethodId);
      await fetchPaymentMethods();
    } catch (error) {
      setError('Failed to delete payment method');
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Order History
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Company Profile
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment')}
          >
            Payment Methods
          </button>
        </li>
      </ul>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title mb-4">Order History</h5>
            {orders.map(order => (
              <div 
              key={order.orderId} 
              className="card mb-3 cursor-pointer hover-shadow"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                  navigate(`/wholesale/orders/${order.orderID}`);
              }}
          >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">Order #{order.orderID}</h6>
                      <p className="text-muted mb-0">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </p>
                      <p className="mb-0">
                        {order.itemCount} items · ${Number(order.totalAmount).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <span className={`badge ${
                        order.orderStatus === 'completed' ? 'bg-success' :
                        order.orderStatus === 'processing' ? 'bg-primary' :
                        'bg-secondary'
                      }`}>
                        {order.orderStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && profile && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title mb-4">{profile.companyName}</h5>
            <div className="row">
              <div className="col-md-6">
                <h6>Company Information</h6>
                <p><strong>Company Name:</strong> {profile.companyName}</p>
                <p><strong>Contact:</strong> {profile.firstName} {profile.lastName}</p>
                <p><strong>Email:</strong> {profile.email}</p>
                <p><strong>Phone:</strong> {profile.phone}</p>
              </div>
              <div className="col-md-6">
                <h6>Shipping Address</h6>
                <p>{profile.addressLine}</p>
                <p>{profile.city}, {profile.state} {profile.zip}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Methods Tab */}
      {activeTab === 'payment' && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title mb-4">Payment Methods</h5>
            
            {/* Add New Card Button */}
            <button
              className="btn btn-primary mb-4"
              onClick={() => setShowNewCardForm(!showNewCardForm)}
            >
              {showNewCardForm ? 'Cancel' : 'Add New Card'}
            </button>

            {/* New Card Form */}
            {showNewCardForm && (
              <div className="card mb-4">
                <div className="card-body">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                  <button
                    className="btn btn-primary mt-3"
                    onClick={handleAddPaymentMethod}
                    disabled={!stripe || isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 'Add Card'}
                  </button>
                </div>
              </div>
            )}

            {/* Existing Payment Methods */}
            {paymentMethods.map(method => (
              <div key={method.id} className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0">
                        {method.brand.toUpperCase()} ending in {method.last4}
                      </h6>
                      <small className="text-muted">
                        Expires {method.exp_month}/{method.exp_year}
                      </small>
                    </div>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDeletePaymentMethod(method.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap with Stripe Elements
const OrderHistoryWrapper = () => (
  <Elements stripe={stripePromise}>
    <OrderHistory />
  </Elements>
);

export default OrderHistoryWrapper;